import { useRef, useEffect } from 'react';
import type { ReactElement } from 'react';
import { json, type LoaderFunctionArgs } from '@remix-run/node';
import { useLoaderData, useLocation, useSearchParams } from '@remix-run/react';
import invariant from 'tiny-invariant';
import { cn } from '@rocco/utils/cn';

import { prefetchCategoryImages } from '~/modules/category/util/prefetch-category-images';
import {
    createMetaDescription,
    createMetaTitle,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { fullCountryNameFromCode } from '~/modules/seo/util/localisation';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { Box, Container, useStyles } from '@archipro-design/aria';
import { NoResultsSection } from '@modules/category/component/no-results/NoResultsSection';
import {
    querySearchServiceAggregate,
    querySearchServiceSearch,
} from '~/modules/search';
import type { ProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { mapESProjectTileViewModel } from '@modules/search/util/elasticsearch-project-tile-view';
import { CategoryFilterSection } from '@modules/category/component/category-filter-section/CategoryFilterSection';
import { createESAggregationProps } from '@modules/search/util/create-es-aggregation-props';
import {
    createEmptyFilterStates,
    createFilterStates,
} from '~/modules/search/util/create-filter-states';
import { getDirectoryPageMeta } from '@modules/category/api';
import type { AdsLoaderData } from '@modules/adverts/api';
import { adRequest, adsRequest } from '@modules/adverts/api';
import { createPaginationState } from '@modules/pagination/util/create-pagination-state.server';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';
import { useAppData } from '~/modules/root/hook/use-app-data';
import { TitleSectionDesktop } from '~/modules/category/component/title-section/TitleSection.desktop';
import { TitleSectionMobile } from '~/modules/category/component/title-section/TitleSection.mobile';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getString, parseURL } from '@archipro-website/top-navigation';
import { CategoryFilterNav } from '@modules/category/component/category-filter-section/CategoryFilterNav';
import { CollapsiblePageLayout } from '~/modules/category/component/collapsible-page-layout/CollapsiblePageLayout';
import { useSearchFilters } from '~/modules/search/hook/use-search-filters';
import AppliedFilters from '@modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '@modules/search/util/reset-filter-states';
import { useTracker } from '@archipro-website/tracker';
import { getDirectoryFullName } from '~/modules/category/util/directory-helpers';
import { validateSort } from '~/modules/search/util/apply-es-search-sort';
import { useFilterDrawerPreference } from '@modules/search/hook/use-filter-drawer-preference';
import { getSession } from '~/modules/root/util/session.server';
import { useDeleteItemWithConfirm } from '~/modules/directory/hook/use-delete-item-with-confirm';
import TitleSectionFilterMobile from '~/modules/category/component/title-section/TitleSectionFilter.mobile';
import { shuffleArray } from '~/utils/arrayHelper';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getProjectCategories } from '~/modules/projects-category/api';
import * as S from '~/modules/projects-category/components/project-category-container/ProjectCategoryContainer.style';
import { advertCreativeProjectTileView } from '@modules/products-category/util/advert-creative-project-tile-view';
import { getProjectPlacements } from '~/modules/adverts/utils/project-placement-helper.server';
import { parseURLToESQueryParams } from '~/modules/search/util/filter-url-helpers';
import { getRouteData } from '~/modules/root/util/get-route-data';
import {
    DIRECTORY_IMAGE_WIDTH_DESKTOP_THIRDS,
    DIRECTORY_IMAGE_WIDTH_MOBILE_THIRDS,
} from '~/modules/root/util/get-directory-tile-image-props';
import {
    hasFeature,
    useFeature,
    useTileHotButtons,
    type APHandle,
} from '~/modules/root';
import getShowProfessionalArchiproConnectBanner from '~/utils/get-show-professional-archipro-connect-banner';

import { getAppliedFilterNumber } from '~/modules/search/util/filter-state-util';
import { getUrlHasFilters } from '~/modules/search/util/get-url-has-filters.server';
import { professionalEvenShuffleAds } from '~/utils/adHelper';
import BackToTopButton from '@modules/directory/component/BackToTopButton';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { noIndexProjectCategory } from '@modules/seo/util/no-index-project-category';
import { getHeroPotm } from '@modules/projects-category/api/get-hero-potm.server';
import DirectoryHeroPotmDesktop from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmDesktop';
import { getGridPotms } from '@modules/projects-category/api/get-grid-potms.server';
import { getPreloadImageLink } from '@modules/images';
import DirectoryHeroPotmMobile from '@modules/projects-category/components/directory-hero-potm/DirectoryHeroPotmMobile';
import { usePotmHeroEnabled } from '@modules/projects-category/util/potm-hero-enabled';
import { buildProjectTileGrid } from '~/utils/buildProjectTileGrid';
import { renderProjectTileGrid } from '~/utils/renderProjectTileGrid';
import { getProjectsQuerySize } from '~/utils/get-projects-query-size';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import { mapCategoryCountToPlaceholder } from '~/modules-rocco/categories/data-mappers/category-count-to-placeholder';
import { getActiveDirectoryCategoriesCount } from '~/modules-rocco/categories/api/get-active-directory-categories-count.server';
import type { ProjectTileViewModel as ProjectTileViewModelRocco } from '@rocco/ui/tile-project/view-models/project';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import { getProjectsFromESSearchResults } from '~/modules-rocco/search/data-mappers/tile-project';
import { SearchIndexes } from '~/modules-rocco/search/types/enum/search-indexes';
import { CategoryPageSearchIsland } from '~/ui-rocco/pages/category-page/CategoryPageSearchIsland';
import { CategoryPageRecentlyViewed } from '~/ui-rocco/pages/category-page/CategoryPageRecentlyViewed';
import { getRecentlyViewedServer } from '~/modules-rocco/recently-viewed/api/get-recently-viewed.server';
import { mapProjectToTileViewModel } from '~/modules-rocco/projects/data-mappers/tile-project';
import { useCategoryTilesTracking } from '@ui-rocco/hooks/use-category-tiles-tracking';
import { Pagination as TilePagination } from '@rocco/ui/pagination/components/Pagination';
import { buildPaginationUrl } from '~/modules-rocco/pagination/util/build-pagination-url';
import { CategoryPageFAQ } from '~/ui-rocco/pages/category-page/CategoryPageFAQ';
import { PageBottomSection } from '~/ui-rocco/pages/shared/PageBottomSection';
import { CollapsibleToggle } from '~/modules/category/component/collapsible-page-layout/CollapsibleToggle';
import { useOpenLogin } from '~/utils-rocco/hooks/open-login-modal';
import { VirtualizedGrid } from '~/components/VirtualizedGrid';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

export const PROJECT_TILE_PAGING_WIDTH = 175;
export const PROJECT_TILE_BULLET_SIZE = 11;
export const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
export const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data, matches } = props;

        const { rootData } = getRouteData(matches);

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.directoryPageMeta?.category
                    ? `${data?.directoryPageMeta?.category} projects`
                    : 'Page Not Found',
            ],
            true,
            data?.pagination?.page
        );

        invariant(
            rootData?.localisation.country,
            'rootData is required for product category SEO'
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Featuring ${
                data?.directoryPageMeta?.category
            } projects from the most talented professionals in ${fullCountryNameFromCode(
                rootData.localisation.country
            )} with stunning photos and amazing designs to inspire you.`,
        ]);

        const numberTilesToPreload = data?.appDisplayMode === 'desktop' ? 3 : 2;
        let preloadImageLinks = data?.searchResult?.hits
            ? prefetchCategoryImages(
                  data.searchResult.hits,
                  numberTilesToPreload,
                  getImagesSizes({
                      desktop: DIRECTORY_IMAGE_WIDTH_DESKTOP_THIRDS,
                      mobile: DIRECTORY_IMAGE_WIDTH_MOBILE_THIRDS,
                  }),
                  false
              )
            : [];

        if (data?.heroPotm?.videoCoverDesktop) {
            const preloadHero = getPreloadImageLink(
                data.heroPotm.videoCoverDesktop,
                undefined,
                getImagesSizes(1920)
            );
            preloadImageLinks = [...preloadHero, ...preloadImageLinks];
        }

        const metaDescriptors = [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];

        return noIndexProjectCategory(metaDescriptors, data?.url ?? '');
    }
);

export const handle: APHandle = {
    isCategoryPage: true,
    showStickyNavAfterPosition: 500,
    hideContactArchiPro: false,
};

const DEFAULT_PROJECT_PAGE_SIZE = 36;
const MAX_ADS_PER_PAGE = 3;
const MAX_VERTICAL_TILES_PER_PAGE = 4;
const MAX_VERTICAL_AD_TILES_PER_PAGE = 1;
const MAX_SPONSORED_AD_TILES_DISPLAYED = 3;
export const PROJECT_TILE_EXPANDED_WIDTH = 613;
export const PROJECT_TILE_COLLAPSED_WIDTH = 455;

export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const { appDisplayMode, user, featureFlags } = context;
    const isMobile = appDisplayMode === 'mobile';
    const url = new URL(request.url);
    const searchParams = url.searchParams;
    const pageParam = Number(searchParams.get('page'));
    const page = pageParam && isFinite(pageParam) ? pageParam : 1;
    const { basePath, params } = parseURL(url.pathname);
    const urlSort = validateSort(getString(params, 'sort'));
    const session = await getSession(request);
    const hasFilters = getUrlHasFilters(url);

    const queryES = async (pageSize: number) => {
        const { queryParams } = parseURLToESQueryParams(
            `${url.pathname}?${searchParams.toString()}`,
            'project',
            'directory',
            { pageSize },
            'featured'
        );
        const isFoo = context.config.search.domain === 'foo';
        const aggregationProps = createESAggregationProps({
            ...queryParams,
            searchParams,
        });
        const searchPromise = !isFoo
            ? querySearchServiceSearch({ context, queryParams })
            : null;
        const aggregationPromise = !isFoo
            ? querySearchServiceAggregate({
                  context,
                  queryParams: aggregationProps,
              })
            : null;

        return Promise.all([
            queryParams,
            aggregationProps,
            searchPromise,
            aggregationPromise,
        ]);
    };

    const getAdsGridPotmsAndEsResults = async () => {
        const [ads, sponsoredAds, gridPotms] = await Promise.all([
            adsRequest({
                type: 'DirectoryWithProjectPlacementGroup',
                requestUrl: request.url,
                ...context,
                request,
            }),
            adRequest({
                type: 'SponsorshipDirectoryPlacement',
                requestUrl: request.url,
                ...context,
                request,
            }),
            getGridPotms(context),
        ]);
        const validVerticalAds = ads?.VerticalTilePlacement?.filter(
            (ad) => !!ad?.id
        );
        const queryESPageSize = getProjectsQuerySize({
            sponsoredAdsCount: sponsoredAds?.length ?? 0,
            gridPotmCount: gridPotms?.length ?? 0,
            verticalAdsCount: validVerticalAds?.length ?? 0,
            hasFilters,
            page,
            config: {
                defaultProjectPageSize: DEFAULT_PROJECT_PAGE_SIZE,
                maxVerticalTilesPerPage: MAX_VERTICAL_TILES_PER_PAGE,
                maxVerticalAdTilesPerPage: MAX_VERTICAL_AD_TILES_PER_PAGE,
                maxSponsoredAdTilesDisplayed: MAX_SPONSORED_AD_TILES_DISPLAYED,
            },
        });
        return {
            ads: {
                ...ads,
                VerticalTilePlacement: validVerticalAds,
            } as AdsLoaderData,
            sponsoredAds,
            gridPotms,
            query: await queryES(queryESPageSize),
        };
    };

    const [
        directoryPageMeta,
        // rootDirectoryPageMeta will be used as a fallback for missing category descriptions
        rootDirectoryPageMeta,
        projectCategory,
        recentlyViewedResult,
        localisation,
        heroPotm,
        activeDirectoryCategoriesCount,
        {
            ads,
            sponsoredAds,
            gridPotms,
            query: [queryParams, aggregationProps, searchResult, aggregations],
        },
    ] = await Promise.all([
        getDirectoryPageMeta({
            url,
            ...context,
        }),
        // fetch the root projects category meta to use its description as a fallback
        getDirectoryPageMeta({
            url: new URL('/projects', request.url),
            ...context,
        }),
        getProjectCategories({
            ...context,
            request,
        }),
        getRecentlyViewedServer({ type: 'Projects' }, context),
        getPageLocalisation({ context, request }),
        getHeroPotm(context),
        getActiveDirectoryCategoriesCount({}, context),
        getAdsGridPotmsAndEsResults(),
    ]);

    const _disableHeroPotm = true;
    const hasHeroPotmMedia =
        !_disableHeroPotm &&
        (isMobile
            ? !!(
                  heroPotm?.videoCoverMobile &&
                  (heroPotm?.mobileVideoMp4 || heroPotm?.mobileVideoWebm)
              )
            : !!(
                  heroPotm?.videoCoverDesktop &&
                  (heroPotm?.teaserVideoMp4 || heroPotm?.teaserVideoWebm)
              ));

    const pagination = createPaginationState({
        request,
        totalCount: searchResult?.nbHits ?? 0,
        maxDisplayCount: searchResult?.maxResultWindow,
        defaultPageSize: DEFAULT_PROJECT_PAGE_SIZE,
    });

    const projects = getProjectsFromESSearchResults(searchResult, user);
    const tiles: TileViewModel<ProjectTileViewModelRocco>[] = projects.map(
        (project) => ({
            aspectRatio: 'default',
            model: project,
        })
    );

    const projectTiles =
        searchResult?.hits?.reduce((tiles, hit) => {
            if (hit.PType === 'Project')
                tiles.push(
                    mapESProjectTileViewModel(hit, 'medium', isMobile, user)
                );
            return tiles;
        }, [] as ProjectTileViewModel[]) ?? [];

    const shuffledAds = ads?.CategoryPlacement
        ? shuffleArray(ads.CategoryPlacement)
        : [];

    const verticalTileAds = ads?.VerticalTilePlacement
        ? shuffleArray(ads.VerticalTilePlacement)
        : [];

    const filterStates = aggregations
        ? createFilterStates({
              aggregations,
              appliedFilters: queryParams.filters,
              aggregationsParams: aggregationProps.aggregations,
              searchType: 'project',
              visibilityType: 'directory',
              categoryLink: basePath,
              rootText: 'All',
              sortBy: urlSort,
          })
        : createEmptyFilterStates({ visibilityType: 'directory' });

    const appliedFilterCount = getAppliedFilterNumber(filterStates);
    const sponsoredAdsShuffled = sponsoredAds
        ? professionalEvenShuffleAds(sponsoredAds).slice(
              0,
              MAX_SPONSORED_AD_TILES_DISPLAYED
          )
        : [];

    const sponsoredAdsTiles = sponsoredAdsShuffled.flatMap((advert) => {
        const professionalTile = advertCreativeProjectTileView(advert);
        return professionalTile !== null ? professionalTile : [];
    });

    const projectAdsTiles = ads ? getProjectPlacements(ads, isMobile) : [];

    const enableAwardBadges = hasFeature(featureFlags, 'award_badges');

    return json({
        userPreferenceSession: session.getUserPreferences(),
        appDisplayMode,
        directoryPageMeta,
        projectCategory,
        projectTiles,
        pagination,
        searchResult,
        recentlyViewedItems:
            recentlyViewedResult?.RecentlyViewedProjects?.map((project) =>
                mapProjectToTileViewModel(project, {
                    appDisplayMode,
                    imageToDisplay: 'featured',
                })
            ) ?? [],
        filterStates,
        // If directory doesn't have a description, use the root category description as a fallback
        categoryDescription:
            directoryPageMeta?.directoryHeaderDescription ||
            rootDirectoryPageMeta?.directoryHeaderDescription ||
            'ArchiPro features the largest selection of completed architectural projects in New Zealand. These building and renovation accomplishments showcase the very best in design ingenuity and talent. Browse through these inspirational projects and connect with the professionals involved directly via ArchiPro.',
        ads: shuffledAds,
        verticalTileAds,
        sponsoredAds: sponsoredAdsTiles,
        projectAds: projectAdsTiles.slice(0, MAX_ADS_PER_PAGE),
        localisation,
        enableAwardBadges,
        appliedFilterCount,
        url: url.pathname + url.search,
        heroPotm: _disableHeroPotm ? undefined : heroPotm,
        gridPotms,
        hasHeroPotmMedia: hasHeroPotmMedia,
        pageTheme: hasHeroPotmMedia ? 'transparent' : 'default',
        projectTilesRocco: tiles,
        searchIslandPlaceholders: mapCategoryCountToPlaceholder(
            activeDirectoryCategoriesCount?.projects ?? [],
            `Search projects`,
            'projects'
        ),
    });
};

const ProjectsCategoryPage = () => {
    const { user } = useAppData();
    const { css } = useStyles();
    const appDisplayMode = useAppDisplayModeContext();
    const { desktop, mobile } = appDisplayMode;
    const { isSafari } = useDeviceBrowserContext();
    const filtersRef = useRef<HTMLDivElement | null>(null);
    const {
        categoryDescription,
        directoryPageMeta,
        projectTiles,
        pagination,
        recentlyViewedItems,
        filterStates,
        ads,
        verticalTileAds,
        sponsoredAds,
        projectAds,
        enableAwardBadges,
        appliedFilterCount,
        heroPotm,
        gridPotms,
        hasHeroPotmMedia,
        searchIslandPlaceholders,
        projectTilesRocco,
    } = useLoaderData<typeof loader>();
    const tracker = useTracker();
    const [searchParams] = useSearchParams();
    const { setCategory } = useCurrentCategory();
    // Keep the first value returned by loader to avoid changing when loader revalidate
    const adsMemoized = useLocationMemo(ads);
    const verticalTileAdsMemoized = useLocationMemo(verticalTileAds);
    const sponsoredAdsMemoized = useLocationMemo(sponsoredAds);
    const projectAdsMemoized = useLocationMemo(projectAds);
    const remixLocation = useLocation();
    const projectTilesRoccoAdmin = useTilesAdminData(
        projectTilesRocco as TileViewModel<ProjectTileViewModelRocco>[]
    );

    // init category tiles tracking
    const gridRef = useRef<HTMLDivElement | null>(null);
    useCategoryTilesTracking(gridRef);

    const projectsRef = useRef<HTMLDivElement>(null);
    const selectedCategory = getDirectoryFullName(directoryPageMeta);
    const selectedRequirements = searchParams.getAll('services').join(' | ');
    const selectedProfessions = searchParams
        .getAll('professionals')
        .join(' | ');
    useEffect(() => {
        tracker.log('projectListImpression', {
            label: 'Dentsu',
            url: new URL(window.location.href),
            data: {
                category: selectedCategory,
                requirement: selectedRequirements,
                professionals: selectedProfessions,
            },
        });
    }, [selectedCategory, selectedProfessions, selectedRequirements, tracker]);

    const { filterDrawerOpen, toggleFilterDrawer } =
        useFilterDrawerPreference();

    const {
        itemID: itemIDToDelete,
        willDelete,
        confirmDelete,
        cancelDelete,
    } = useDeleteItemWithConfirm();

    const projectIDs = projectTiles.map((project) => project.tile.projectID);
    const sponsoredAdsIDs = sponsoredAdsMemoized.map(
        (project) => project.projectId
    );

    const {
        getTileTopActions,
        hotButtonModal,
        getTileAdminCaption,
        canEditAdmin,
    } = useTileHotButtons({
        itemType: 'project',
        itemIDs: [...projectIDs, ...sponsoredAdsIDs],
    });

    const showSlider = useABTestIsOn('directory-tile-slider');
    const isStaffUser = user.__typename === 'Me' && user.IsArchiproStaff;
    const showViewCount = useABTestIsOn('project_tile_stats') && isStaffUser;

    //potms feature flag conditions
    const directoryPotmEnbaled = useABTestIsOn('directory-potms');
    const potmFeatureOn = usePotmHeroEnabled();
    const heroPotmsEnabled = potmFeatureOn && heroPotm;

    // check if there are both cover image and videos available
    const showHeroPotm = !!heroPotmsEnabled && hasHeroPotmMedia;

    const searchFilters = useSearchFilters({
        category: 'project',
        initialStates: filterStates,
        isCategoryPage: true,
        ignoreFilterKeys: ['viewProjectsBy', 'awards'],
    });

    const apConnectBannerFlag = useFeature('archipro_connect_category_banner');

    const showProfessionalArchiproBanner =
        getShowProfessionalArchiproConnectBanner(user);

    const sortedGridTiles = buildProjectTileGrid({
        showArchiproConnectBanner:
            showProfessionalArchiproBanner && apConnectBannerFlag,
        desktop,
        ads: adsMemoized,
        verticalTileAds: verticalTileAdsMemoized,
        sponsoredAds: sponsoredAdsMemoized,
        projectAds: projectAdsMemoized,
        potmFeatureOn: directoryPotmEnbaled,
        projectTiles: projectTilesRoccoAdmin,
        gridPotms,
        appliedFilterCount,
        pagination,
        columnCount: desktop ? (filterDrawerOpen ? 3 : 4) : 1,
    });

    const tileGridItems: ReactElement[] = renderProjectTileGrid({
        tiles: sortedGridTiles,
        showSlider,
        mobile,
        getTileTopActions,
        willDelete,
        getTileAdminCaption,
        filterDrawerOpen,
        desktop,
        showViewCount,
        enableAwardBadges,
        canEditAdmin,
    }) as ReactElement[];

    const openLogin = useOpenLogin();

    // Check if the UI updates feature flag is enabled
    const uiUpdatesJune2025Enabled = hasFeature(
        user.FeatureFlags,
        'UI_updates_june_2025'
    );

    useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <Box>
            {showHeroPotm &&
                (desktop ? (
                    <DirectoryHeroPotmDesktop
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ) : (
                    <DirectoryHeroPotmMobile
                        {...heroPotm}
                        projectsCount={pagination.totalCount}
                        projectsRef={projectsRef}
                    />
                ))}
            <Container
                ref={projectsRef}
                className={css(S.ProjectCategoryContainer)}
            >
                {desktop ? (
                    <TitleSectionDesktop
                        category={directoryPageMeta.category}
                        rootCategory="Projects."
                        categoryDescription={categoryDescription}
                        itemsInCategory={filterStates.totalCount}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        showCategoryTitle={!heroPotmsEnabled}
                        isRoot={directoryPageMeta.isRoot}
                    />
                ) : (
                    <TitleSectionMobile
                        title={directoryPageMeta.category}
                        categoryDescription={categoryDescription}
                        subtitle={''}
                        showCategoryTitle={!showHeroPotm}
                    >
                        <TitleSectionFilterMobile
                            esCategory="project"
                            searchFilters={searchFilters}
                        />
                    </TitleSectionMobile>
                )}

                {desktop && (
                    <CategoryFilterSection
                        category="project"
                        searchFilters={searchFilters}
                        breadcrumbs={directoryPageMeta.breadcrumbs}
                        filterDrawerOpen={filterDrawerOpen}
                        componentBefore={
                            <div ref={filtersRef}>
                                <CollapsibleToggle
                                    filterDrawerOpen={filterDrawerOpen}
                                    toggleFilterDrawer={toggleFilterDrawer}
                                />
                            </div>
                        }
                    />
                )}

                <CollapsiblePageLayout
                    sidePanel={
                        desktop ? (
                            <CategoryFilterNav
                                category="project"
                                searchFilters={searchFilters}
                                hideCategorySectionTitle={true}
                                hideCategoryBackButton={true}
                            />
                        ) : null
                    }
                    hideSidePanel={!filterDrawerOpen}
                >
                    <div>
                        {desktop &&
                            searchFilters.appliedFilters &&
                            searchFilters.appliedFilters.length > 0 && (
                                <AppliedFilters
                                    filterItems={searchFilters.appliedFilters}
                                    resetFilters={() =>
                                        searchFilters.resetFilters(
                                            resetFilterStates(
                                                searchFilters.filterStates,
                                                ['category']
                                            )
                                        )
                                    }
                                />
                            )}

                        {sortedGridTiles.length ? (
                            <VirtualizedGrid
                                items={tileGridItems}
                                renderItem={(item: React.ReactNode) => item}
                                className={cn(
                                    'grid transition-all duration-300 ease-in-out',
                                    filterDrawerOpen
                                        ? 'grid-cols-1 md:grid-cols-3'
                                        : 'grid-cols-1 md:grid-cols-4',
                                    'gap-x-2 gap-y-12 md:gap-x-6 md:gap-y-20'
                                )}
                                enabled={isSafari}
                                initialVisibleCount={15}
                                batchSize={10}
                                loadMoreThreshold={0.7}
                                reduceThreshold={0.3}
                                minVisibleCount={15}
                                startMarker={
                                    <div className="hidden">
                                        <CollapsibleToggle
                                            filterDrawerOpen={filterDrawerOpen}
                                            toggleFilterDrawer={
                                                toggleFilterDrawer
                                            }
                                        />
                                    </div>
                                }
                                endMarker={
                                    pagination.totalPages > 1 && (
                                        <div className="pt-[6.5625rem] md:pt-20">
                                            <TilePagination
                                                currentPath={remixLocation}
                                                current={pagination.page}
                                                total={pagination.totalPages}
                                                urlBuilder={buildPaginationUrl}
                                                visibleCount={
                                                    appDisplayMode.desktop
                                                        ? 4
                                                        : 3
                                                }
                                            />
                                        </div>
                                    )
                                }
                            />
                        ) : (
                            <NoResultsSection resultsType={'projects'} />
                        )}
                    </div>
                </CollapsiblePageLayout>

                <div className="my-10 flex flex-col gap-10 md:my-[12.8125rem] md:gap-[13.75rem]">
                    <CategoryPageRecentlyViewed
                        displayMode={
                            appDisplayMode.desktop ? 'desktop' : 'mobile'
                        }
                        items={recentlyViewedItems}
                    />

                    <CategoryPageFAQ
                        faqSchemas={directoryPageMeta.faqSchemas}
                        footerContent={directoryPageMeta.footerContent}
                    />

                    {desktop && <PageBottomSection openLogin={openLogin} />}
                </div>

                {itemIDToDelete && (
                    <DeleteItemConfirmModal
                        title={'Delete project'}
                        content={
                            'Are you sure you would like to delete this project? All information and associated tags will be lost.'
                        }
                        open={true}
                        onCancel={cancelDelete}
                        onConfirm={confirmDelete}
                    />
                )}
                {hotButtonModal}
            </Container>
            <BackToTopButton />

            {!uiUpdatesJune2025Enabled && (
                <CategoryPageSearchIsland
                    searchIndex={SearchIndexes.Projects}
                    displayMode={appDisplayMode.desktop ? 'desktop' : 'mobile'}
                    placeholders={searchIslandPlaceholders}
                    scrollThreshold={showHeroPotm ? 200 : 0}
                    forceHide={filterDrawerOpen}
                />
            )}
        </Box>
    );
};

export default ProjectsCategoryPage;
